// Timeline section helper (W3.1 §5.7).
//
// Pure helpers for the 3-section timeline (過去 / 現在 / 未來). Backend
// already returns `YearSection[]` with `is_past_section` / `is_future_section`
// flags + `is_collapsed_default`. This module adds two client-only concerns:
//
// 1. `splitYearsBySection(years)` — bucket by is_past_section / is_future_section.
// 2. `isCollapsedDefault(section, userToggled)` — collapse rules per spec §5.1:
//
//   • 過去 section: 預設永遠展開 (per user grill 2026-09-24). UI may still
//     collapse when userToggled[year] === true.
//
//   • 現在 section: 永遠展開 (anchor of the layout).
//
//   • 未來 section: 預設折疊 (collapsed until user clicks 「展開未來」 or
//     individual year chevron).
//
// 3. `dedupeSameDay(events)` — same-day dedup per spec §4.3 step 6
//    (anchor > milestone > custom). Used by TimelineEventCard to keep
//    cards from stacking 3 deep on the same date.

import { parseISO } from "date-fns";

export interface TimelineYearSection {
  year: number;
  label: string;
  events: TimelineEventLite[];
  is_now: boolean;
  is_past_section: boolean;
  is_future_section: boolean;
  is_collapsed_default: boolean;
}

export interface TimelineEventLite {
  id: string;
  kind: "anniversary" | "milestone";
  title?: string;
  date: string;
  is_anchor: boolean;
  /**
   * W45.0 `ADR-0114` — the parent anniversary, for `kind: "milestone"` only.
   *
   * It has to be declared **here** and not only on the API type: `dedupeSameDay`
   * rebuilds each event as a literal, so a field missing from this interface is
   * a field silently dropped before `TimelineEventCard` ever sees it — and the
   * symptom would be a milestone that quietly stopped being tappable, which is
   * the exact defect this round is fixing.
   */
  anniversary_id?: string | null;
  /** Numeric priority used for same-day dedup. Lower wins. */
  priority?: number;
  /** Category tag (only present for `kind === "anniversary"`; null for milestones). */
  category?: string | null;
  /**
   * Days from today to the event's calendar date. Negative = past, positive =
   * future, 0 = today. Sourced from the backend timeline API; preserved here
   * so the rendering layer doesn't have to recompute on the client.
   */
  days_until?: number;
  /** Backend-computed flag for 「今天」 highlight; optional fallback for legacy data. */
  is_today?: boolean;
}

export interface SectionBuckets {
  past: TimelineYearSection[];
  now: TimelineYearSection[];
  future: TimelineYearSection[];
}

/** Bucket the years array into 3 sections per the per-year flags. */
export function splitYearsBySection(
  years: readonly TimelineYearSection[],
): SectionBuckets {
  const past: TimelineYearSection[] = [];
  const now: TimelineYearSection[] = [];
  const future: TimelineYearSection[] = [];
  for (const y of years) {
    if (y.is_past_section) past.push(y);
    else if (y.is_future_section) future.push(y);
    else now.push(y);
  }
  return { past, now, future };
}

/**
 * Compute the collapsed flag at render time:
 *   - past: NEVER collapse (per spec §5.1 — 全部展開, user grill 2026-09-24)
 *   - now: NEVER collapse (the layout anchor)
 *   - future: collapse when year is_collapsed_default AND user hasn't toggled
 *
 * `userToggled` is a Map<year, boolean>; true = user forced expand.
 * Only honoured for future sections (past / now always show).
 */
export function isCollapsedDefault(
  section: TimelineYearSection,
  userToggled: ReadonlyMap<number, boolean> = new Map(),
): boolean {
  if (section.is_past_section) return false;
  if (section.is_future_section) {
    const userValue = userToggled.get(section.year);
    if (userValue !== undefined) return !userValue;
    return section.is_collapsed_default;
  }
  // now section: never collapsed
  return false;
}

/**
 * Sort key used by the same-day dedup. Anchor has highest priority (kept),
 * then milestones, then everything else. Per W3.1 §4.3 step 6.
 */
function eventPriority(ev: TimelineEventLite): number {
  if (ev.priority !== undefined) return ev.priority;
  if (ev.is_anchor) return 0;
  if (ev.kind === "milestone") return 1;
  return 2;
}

/**
 * Deduplicate events that share a calendar date (in HKT). Keeps the highest
 * priority row per date. Stable on original order.
 */
export function dedupeSameDay(
  events: readonly TimelineEventLite[],
): TimelineEventLite[] {
  const byDate = new Map<string, TimelineEventLite>();
  for (const ev of events) {
    const d = parseISO(ev.date);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const existing = byDate.get(key);
    if (!existing || eventPriority(ev) < eventPriority(existing)) {
      byDate.set(key, ev);
    }
  }
  // Preserve original encounter order so downstream rendering stays stable.
  const seen = new Set<string>();
  const out: TimelineEventLite[] = [];
  for (const ev of events) {
    const d = parseISO(ev.date);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (byDate.get(key) === ev && !seen.has(key)) {
      seen.add(key);
      out.push(ev);
    }
  }
  return out;
}